import React, { useContext, useEffect, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSettings } from 'services/settings/SettingsContext';
import ListContext from 'components/lists/ListContext';
import { buildBulkActionFilters } from 'components/lists/functions/selectionUtils';
import { useAllowedRight } from 'services/permissions/permissionChecks';
import { useBulkActionRunner } from './functions/useBulkActionRunner';
import BulkActionConfirmStep from './BulkActionConfirmStep';
import BulkActionProgress from './BulkActionProgress';
import { BulkActionKind, BulkActionPanelStep, BulkActionsConfig, BulkCustomActionConfig, BulkExportLevel } from 'types/BulkActionTypes';
import SecondaryButton from 'components/buttons/SecondaryButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import '../../style/scss/bulkActions.scss';

/*
 * BulkActionsPanel.tsx
 * Fixed right-edge panel shown whenever a list with bulkActions enabled has
 * at least one selected row. Not a ModalContext modal, since the user must
 * be able to keep (un)checking rows in the underlying list while it is open.
 */

interface BulkActionsPanelProps {
    bulkActions: BulkActionsConfig;
    initialKind?: 'export'; // Opens the panel directly on the confirm step of this action
    restoreMode?: boolean; // The list shows deleted or deactivated items, restore is the only action
}

// Reads the bottom edge of the sub navbar (or the navbar itself when no
// sub navbar is active) in the viewport. The nav chrome is sticky at the
// top of the viewport (see navigation.scss), so this is the fixed top
// offset the panel starts at for any scroll position
const getNavChromeHeight = () => {
    const subnavbar = document.querySelector('.subnavbar');
    const navbarWrapper = document.querySelector('.navbar-wrapper');
    const bottom = (subnavbar ?? navbarWrapper)?.getBoundingClientRect().bottom ?? 0;
    return Math.max(0, bottom);
};

const BulkActionsPanel: React.FC<BulkActionsPanelProps> = ({ bulkActions, initialKind, restoreMode }) => {
    const { t } = useTranslation();
    const { userLocale } = useSettings();
    const hasRightCheck = useAllowedRight;

    const { selection, setSelection, selectedCount, queryParams, refetchList, objectName } = useContext(ListContext);
    const runner = useBulkActionRunner(bulkActions.objectKey);

    // Resolve the singular or plural object name based on the selected count
    const selectedObjectName = objectName
        ? t(`${objectName}.${selectedCount === 1 ? 'singular' : 'plural'}`)
        : '';

    // Format the selected count with locale aware thousand separators
    const formattedSelectedCount = selectedCount.toLocaleString(userLocale);

    // Height of the nav chrome the panel content should start below
    const [navChromeHeight, setNavChromeHeight] = useState<number>(getNavChromeHeight);

    // Remeasure the nav chrome height whenever it changes size, for
    // example when the window resizes or a notification banner closes
    useLayoutEffect(() => {
        const measureNavChromeHeight = () => setNavChromeHeight(getNavChromeHeight());
        measureNavChromeHeight();

        // Observe the navbar wrapper since the banner and sub navbar live inside it
        const navbarWrapper = document.querySelector('.navbar-wrapper');
        const resizeObserver = navbarWrapper ? new ResizeObserver(measureNavChromeHeight) : null;
        if (navbarWrapper && resizeObserver) {
            resizeObserver.observe(navbarWrapper);
        }
        window.addEventListener('resize', measureNavChromeHeight);

        return () => {
            resizeObserver?.disconnect();
            window.removeEventListener('resize', measureNavChromeHeight);
        };
    }, []);

    const [step, setStep] = useState<BulkActionPanelStep>('selected');
    const [pendingKind, setPendingKind] = useState<BulkActionKind | null>(null);
    const [pendingCustomAction, setPendingCustomAction] = useState<BulkCustomActionConfig | null>(null);
    const [exportFormat, setExportFormat] = useState<'xlsx' | 'csv'>('xlsx');
    const [exportLevel, setExportLevel] = useState<BulkExportLevel>('document');

    // Jump directly to the confirm step when the panel is opened with an initial action
    useEffect(() => {
        if (initialKind && step === 'selected') {
            setPendingKind(initialKind);
            setStep('confirming');
        }
    }, [initialKind]);

    // Move on to the done step once the running bulk action reaches a final status
    useEffect(() => {
        if (step === 'running' && runner.status && runner.status !== 'in_progress') {
            setStep('done');
        }
    }, [runner.status, step]);

    // Open the confirm step for the delete or deactivate action
    const handleDeleteOrDeactivateClick = () => {
        setPendingKind(bulkActions.deleteOrDeactivate ?? 'delete');
        setStep('confirming');
    };

    // Open the confirm step for the restore action
    const handleRestoreClick = () => {
        setPendingKind('restore');
        setStep('confirming');
    };

    // Open the confirm step for the export action
    const handleExportClick = () => {
        setPendingKind('export');
        setStep('confirming');
    };

    // Open the confirm step for a model-specific custom action, or run a frontend action with the selected ids
    const handleCustomActionClick = (customAction: BulkCustomActionConfig) => {
        if (customAction.onSelect) {
            customAction.onSelect(Array.from(selection.ids));
            return;
        }
        setPendingKind('custom');
        setPendingCustomAction(customAction);
        setStep('confirming');
    };

    // Determine whether a frontend custom action is disabled for the current selection
    const getCustomActionDisabled = (customAction: BulkCustomActionConfig) => {
        if (!customAction.onSelect) return undefined;

        // A frontend action needs an explicit selection within its allowed range
        const outOfRange = selection.mode !== 'include'
            || (customAction.minSelection !== undefined && selectedCount < customAction.minSelection)
            || (customAction.maxSelection !== undefined && selectedCount > customAction.maxSelection);
        return outOfRange ? { disabled: true, message: customAction.disabledMessage } : undefined;
    };

    // Cancel the confirm step and return to the plain selection view
    const handleCancelConfirm = () => {
        setPendingKind(null);
        setPendingCustomAction(null);
        setStep('selected');
    };

    // Confirm the pending action, starts the backend bulk action task
    const handleConfirm = async () => {
        if (!pendingKind) return;

        const filters = buildBulkActionFilters(queryParams);
        const started = await runner.start({
            kind: pendingKind,
            filters,
            selection,
            exportFormat: pendingKind === 'export' ? exportFormat : undefined,
            exportLevel: pendingKind === 'export' ? exportLevel : undefined,
            actionKey: pendingKind === 'custom' ? pendingCustomAction?.key : undefined,
        });

        // Only move on to the running step when the task actually started
        if (started) {
            setStep('running');
        }
    };

    // Close the finished bulk action, refetches the list and clears the selection
    const handleClose = () => {
        refetchList();
        setSelection({ mode: 'include', ids: new Set() });
        runner.reset();
        setStep('selected');
        setPendingKind(null);
        setPendingCustomAction(null);
    };

    // Close the panel from the header cross, either clears the pending action or the whole selection
    const handleHeaderClose = () => {
        if (step === 'done') {
            handleClose();
        } else {
            setPendingKind(null);
            setPendingCustomAction(null);
            setSelection({ mode: 'include', ids: new Set() });
            setStep('selected');
        }
    };

    return (
        <div className='bulk-actions-panel' style={{ top: `${navChromeHeight}px` }}>
            <div className='bulk-actions-panel-header'>
                <h4>
                    {t('bulk_actions.general.panel_title')}
                </h4>
                {step !== 'running' && (
                    // Hide the close cross while a bulk action is actually running
                    <button
                        type='button'
                        className='bulk-actions-panel-close'
                        onClick={handleHeaderClose}
                        aria-label={t('general.close')}
                    >
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                )}
            </div>

            {step === 'selected' && (
                <div className='bulk-actions-list'>
                    <p className='selection-count-text'>
                        {t('bulk_actions.general.selected_count_label', { count: selectedCount, formattedCount: formattedSelectedCount, objectName: selectedObjectName })}
                    </p>
                    {restoreMode ? (
                        // In the deleted or deactivated items view restore is the only action
                        hasRightCheck('can_delete') && (
                            <SecondaryButton
                                onClick={handleRestoreClick}
                                label='general.revert'
                                size='small'
                            />
                        )
                    ) : (
                        <>
                            {bulkActions.deleteOrDeactivate && hasRightCheck('can_delete') && (
                                <SecondaryButton
                                    onClick={handleDeleteOrDeactivateClick}
                                    label={bulkActions.deleteOrDeactivate === 'delete' ? 'general.delete' : 'general.deactivate'}
                                    size='small'
                                />
                            )}
                            {(bulkActions.showExport ?? true) && hasRightCheck('can_export') && (
                                <SecondaryButton
                                    onClick={handleExportClick}
                                    label='bulk_actions.general.export_label'
                                    size='small'
                                />
                            )}
                            {bulkActions.customActions?.map((customAction) => (
                                <SecondaryButton
                                    key={customAction.key}
                                    onClick={() => handleCustomActionClick(customAction)}
                                    label={customAction.label}
                                    disabled={getCustomActionDisabled(customAction)}
                                    size='small'
                                />
                            ))}
                        </>
                    )}
                </div>
            )}

            {step === 'confirming' && pendingKind && (
                <>
                    <BulkActionConfirmStep
                        objectKey={bulkActions.objectKey}
                        objectNameKey={objectName}
                        kind={pendingKind}
                        selectedCount={selectedCount}
                        customAction={pendingCustomAction ?? undefined}
                        exportFormat={exportFormat}
                        onExportFormatChange={setExportFormat}
                        exportLevel={exportLevel}
                        onExportLevelChange={setExportLevel}
                        onConfirm={handleConfirm}
                        onCancel={handleCancelConfirm}
                        confirmLoading={runner.starting}
                    />
                    {runner.errorMessage && (
                        // Show the start error inline, the confirm step stays open
                        <p className='bulk-actions-start-error'>{t(runner.errorMessage)}</p>
                    )}
                </>
            )}

            {(step === 'running' || step === 'done') && pendingKind && (
                <BulkActionProgress
                    kind={pendingKind}
                    status={runner.status}
                    progress={runner.progress}
                    totalRows={runner.totalRows}
                    processedRows={runner.processedRows}
                    resultSummary={runner.resultSummary}
                    resultFileUrl={runner.resultFileUrl}
                    errorMessage={runner.errorMessage}
                    onClose={handleClose}
                />
            )}
        </div>
    );
};

export default BulkActionsPanel;
